import { useIsRestoring } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useSealedCollectionQuantities } from '@/hooks/collections/useCollectionCrud'
import { useUserQuantities } from '@/hooks/collections/useUserQuantities'
import { useUserFilters } from '@/hooks/filters/useUserFilters'
import { useFilterStore } from '@/store/filterStore'
import type { SealedProductFilters } from '@/types'
import { filterSealedProducts, sortSealedProducts, deriveSealedFilterOptions } from '@/utils/cardFiltering'
import { useSealedProducts } from './useSealedProducts'

export function useFilteredSealedProducts(filters: SealedProductFilters) {
  const isRestoring = useIsRestoring()
  useUserQuantities()
  const { data: allProducts, isLoading, isFetching } = useSealedProducts()
  const { data: quantities } = useSealedCollectionQuantities()
  const { data: userFilters } = useUserFilters()
  const showHidden = useFilterStore((s) => s.showHidden)

  const filterOptions = useMemo(() => (allProducts ? deriveSealedFilterOptions(allProducts) : undefined), [allProducts])

  const products = useMemo(() => {
    if (!allProducts) return []
    const filtered = filterSealedProducts(allProducts, filters, userFilters, showHidden, quantities)
    return sortSealedProducts(filtered, filters.sort, filters.order)
  }, [allProducts, filters, userFilters, showHidden, quantities])

  return {
    products,
    filterOptions,
    totalCount: products.length,
    isLoading: isLoading || isRestoring,
    isFetching,
  }
}
